import { computed, ref, toRaw, watch } from 'vue';
import { defineStore } from 'pinia';
import type { ReplenishmentResponse, ReplenishmentRow } from '@shared/replenishment';
import { HttpError, http } from '@/services/http';
import type { DailySimulationData } from './simulation-export';
import { useActivitiesStore } from '@/features/config/config.store';
import { useSkusStore } from '@/features/skus/skus.store';
import { useAppStore } from '@/stores/app';

const ROWS_CACHE_MS = 15 * 60 * 1000;
const RESPONSE_CACHE_MS = 5 * 60 * 1000;
const RESPONSE_CACHE_MAX = 6;

/* 模拟草稿（「计划出货时间 / 计划出货数量」的 what-if 输入；入仓时间 = 出货 + 船期）原本只存在内存里，
   一旦页面重载（掉登录跳转、手动刷新、浏览器回收标签页）就全丢 —— 用户反馈过。
   这里做本地持久化。

   1.1.96（**方案 A**，用户要求"在他那台电脑上不丢就行"）：
     · **去掉 24 小时自动过期** ✗ —— 原来超过 24h 未编辑就整份删掉，用户第二天回来发现填的全没了。
       现在**一直保留**：只有用户主动点「清空模拟」或「硬刷新」（已加二次确认 ✓）才清 ✓；
     · 多标签页写入改为**与存储里已有的合并**（见 persistManualState ✓），
       不再"后保存的整份覆盖另一个标签页刚填的格子"✗；
     · 写失败（无痕模式 / 企业策略禁用存储 / 配额满）**不再静默吞掉** ✗ → 明确提示一次 ✓。 */
const MANUAL_STATE_KEY = 'replenish_manual_state_v1';
type ManualBatchMap = Record<string, { batches: Array<{ date: string; qty: number; fo?: string }> }>;

/**
 * 上一次写盘时的**内存**快照（plans / drafts ✓）。
 * 用途：写盘时自动求出"本标签页动过哪些键" ✓ —— 与快照不同的键才归本标签页（含"删除"✓）；
 * 相同的键一律让**存储里的值优先**（可能是别的标签页刚填的 ✓，不能被整份覆盖 ✗）。
 * 用快照而不是在每个改动点手工登记 ✓：少一处遗漏就少一个"填了会丢"的口子 ✗。
 */
let lastPersistSnapshot: { plans: ManualBatchMap; drafts: ManualBatchMap } = { plans: {}, drafts: {} };
/** 写失败提示只弹一次（避免每次编辑都弹 ✓） */
let manualPersistWarned = false;

/** 读存储里现有的一份（可能来自别的标签页 ✓）；读不出来就当作空 ✓ */
function readPersistedMaps(): { plans: ManualBatchMap; drafts: ManualBatchMap } {
  try {
    const raw = window.localStorage.getItem(MANUAL_STATE_KEY);
    if (!raw) return { plans: {}, drafts: {} };
    const parsed = JSON.parse(raw) as { plans?: ManualBatchMap; drafts?: ManualBatchMap };
    return { plans: parsed?.plans || {}, drafts: parsed?.drafts || {} };
  } catch {
    return { plans: {}, drafts: {} };
  }
}

function loadManualState(): { manualPlans: ManualBatchMap; manualPlanDrafts: ManualBatchMap } {
  const empty = { manualPlans: {} as ManualBatchMap, manualPlanDrafts: {} as ManualBatchMap };
  try {
    const raw = window.localStorage.getItem(MANUAL_STATE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as { savedAt?: number; plans?: ManualBatchMap; drafts?: ManualBatchMap };
    // 1.1.96: 不再按 savedAt 过期 ✓（老数据照常读出来；savedAt 字段留着以后用 ✓）
    return { manualPlans: parsed?.plans || {}, manualPlanDrafts: parsed?.drafts || {} };
  } catch {
    return empty;
  }
}

/**
 * 1.1.95: 「模拟 / 恢复真实」之后**别把用户滚回表格最上面** ✗ ——
 *   这两个动作会让整表按新数据重排（行序、行高都可能变），浏览器于是把滚动位置重置到顶部 ✗，
 *   用户刚点完就"跳到最上面那行"，得重新找刚才那条 SKU（用户反馈）。
 *
 * 做法：动作前记下所有可能的滚动容器位置 → 数据加载完后**连续几帧**写回去。
 *   ⚠️ 只写一次不够 ✗：行高/行数是分批应用布局的，先写的那次会被后到的布局覆盖。
 */
/** 滚动位置的快照项 —— **存选择器 + 序号，不存元素引用** ✗：
 *   加载态会把明细卡整块销毁重建 ✗，持有旧引用的写入（写到已脱离文档的节点上）等于没写 ✗
 *   → 用户实测："点完模拟还是跳回第一行"（第一版就是踩了这个坑 ✗）。 */
type ScrollSnapshot = { key: string; index: number; top: number };
const SCROLL_KEYS = [
  'window',
  'scrollingElement',
  'main',
  // 备货页真正在滚的是**明细卡**：`.replenishment-page` 里的 `.overflow-auto`
  // （max-height:100vh，见页面样式注释 ✓）—— window 自己反而不滚 ✗，少记它 = 功能像没生效 ✗
  '.replenishment-page .overflow-auto',
  '.rp-scroll',
  '.rp-table-scroll',
];
function captureScrollPositions(): ScrollSnapshot[] {
  const list: ScrollSnapshot[] = [];
  if (typeof window === 'undefined' || typeof document === 'undefined') return list;
  for (const key of SCROLL_KEYS) {
    if (key === 'window') {
      list.push({ key, index: 0, top: window.scrollY || 0 });
      continue;
    }
    if (key === 'scrollingElement') {
      const se = document.scrollingElement;
      if (se) list.push({ key, index: 0, top: se.scrollTop });
      continue;
    }
    Array.from(document.querySelectorAll(key)).forEach((el, index) =>
      list.push({ key, index, top: (el as HTMLElement).scrollTop }),
    );
  }
  return list;
}
function restoreScrollPositions(list: ScrollSnapshot[]): void {
  if (!list.length || typeof window === 'undefined') return;
  const put = (): void => {
    for (const item of list) {
      if (item.key === 'window') {
        window.scrollTo(0, item.top);
        continue;
      }
      if (item.key === 'scrollingElement') {
        const se = document.scrollingElement;
        if (se) se.scrollTop = item.top;
        continue;
      }
      const el = document.querySelectorAll(item.key)[item.index] as HTMLElement | undefined;
      if (el) el.scrollTop = item.top;
    }
  };
  put();
  requestAnimationFrame(() => {
    put();
    requestAnimationFrame(put);
  });
  // 加载态消失是异步的（请求 2~5 秒）→ 在 ~1.8 秒内**反复重写**，
  // 直到容器稳定下来（只写一两次会被后到的"容器重建 + 布局"顶回 0 ✗）。
  let ticks = 0;
  const timer = window.setInterval(() => {
    put();
    if (++ticks >= 15) window.clearInterval(timer);
  }, 120);
  // 用户中途自己滚/操作 → 立刻停手，别跟人抢 ✓
  const cancel = (): void => {
    window.clearInterval(timer);
    window.removeEventListener('wheel', cancel);
    window.removeEventListener('touchstart', cancel);
    window.removeEventListener('keydown', cancel);
  };
  window.addEventListener('wheel', cancel, { passive: true, once: true });
  window.addEventListener('touchstart', cancel, { passive: true, once: true });
  window.addEventListener('keydown', cancel as EventListener, { once: true });
}
/**
 * 1.1.95: 模拟 / 恢复真实 期间 = 忙碌标记。
 * 用途（用户要求"表格原地不动、转圈要悬浮的"）：
 *   ① 页面据此**抑制 LoadingPanel** ✗ —— 原来是整张表被换成加载态，看起来像"表格在动"✗；
 *   ② 页面改成显示一个**悬浮**的小提示条（转圈 + 文案）✓，表格继续显示旧数据 ✓，
 *      新数据到达后就地更新（Vue 只改单元格，不重建整块）✓ —— 滚动位置也就不会再跳 ✓。
 * 用模块级 ref：store 是单例 ✓，helper 在 setup 外定义，拿不到内部的 ref ✗。
 */
const manualPlanBusy = ref(false);

/** 包一层：动作执行（含重新加载数据）完成后，把滚动位置放回去 ✓ */
async function keepScrollWhile<T>(action: () => Promise<T> | T): Promise<T | undefined> {
  const snap = captureScrollPositions();
  manualPlanBusy.value = true;
  try {
    return await action();
  } finally {
    // 稍等一拍再收掉悬浮条：新旧行高交替的一瞬间收掉会闪一下 ✓
    window.setTimeout(() => {
      manualPlanBusy.value = false;
    }, 180);
    restoreScrollPositions(snap);
  }
}

type LoadOptions = {
  silent?: boolean;
};

type PlanRowsRequest = {
  controller: AbortController;
  promise: Promise<ReplenishmentRow[]>;
  sequence: number;
  token: symbol;
};

export const useReplenishmentStore = defineStore('replenishment', () => {
  const sharedActivities = useActivitiesStore();
  const sharedSkus = useSkusStore();
  const rows = ref<ReplenishmentRow[]>([]);
  const total = ref(0);
  const page = ref(1);
  const pageSize = ref(20);
  const totals = ref<ReplenishmentResponse['totals']>({});
  const kpis = ref<Record<string, number>>({});
  const dispCounts = ref<Record<string, number>>({});
  const summary = ref<Record<string, unknown>[]>([]);
  const containers = ref<Record<string, unknown>[]>([]);
  const whCodes = ref<string[]>([]);
  const platforms = ref<string[]>([]);
  const activities = ref<Record<string, unknown>[]>([]);
  const skus = ref<Record<string, unknown>[]>([]);
  const activityIds = ref<number[]>([]);
  const filterOptions = ref<{ factory_regions: string[]; spus: string[]; models: string[]; positions: string[] }>({
    factory_regions: [],
    spus: [],
    models: [],
    positions: [],
  });
  const ratioMeta = ref<Record<string, unknown> | null>(null);
  const planRows = ref<ReplenishmentRow[]>([]);
  const planRowsLoading = ref(false);
  const simulationOpen = ref(false);
  const simulationLoading = ref(false);
  const simulationError = ref('');
  const simulationDays = ref(30);
  const simulationRow = ref<ReplenishmentRow | null>(null);
  const simulation = ref<Record<string, unknown> | null>(null);
  const factoryOrders = ref<Record<string, unknown>[]>([]);
  const factoryOrderGroupSkus = ref<string[]>([]);
  const factoryOrdersLoading = ref(false);
  const factoryOrdersError = ref('');
  const referenceFactoryOrders = ref<Record<string, unknown>[]>([]);
  const referenceFactoryOrdersLoading = ref(false);
  const referenceFactoryOrdersLoaded = ref(false);
  const loading = ref(false);
  const error = ref('');
  const query = ref('');
  const shipType = ref('慢船');
  /* ===== 1.1.113: 年前备货模式（**独立板块/可切换** ✓，关掉 = 与原来完全一致 ✓）=====
     用户口径（2026-10-19）：其他一律不变，只是「工厂某段不生产、某段不发货」→
     那段时间要卖的货必须提前做完发走；两段区间都由用户自己填 ✓、留空 = 该段不生效 ✓。
     参数存浏览器本地（记住上次 ✓），不写库、不影响别人 ✓。 */
  const CNY_HOLD_KEY = 'cny-hold-v1';
  const _cnyInit = (() => {
    try {
      return JSON.parse(window.localStorage.getItem(CNY_HOLD_KEY) || 'null') || {};
    } catch {
      return {};
    }
  })();
  const cnyMode = ref(Boolean(_cnyInit.mode));
  const prodHoldStart = ref(String(_cnyInit.prodStart || ''));
  const prodHoldEnd = ref(String(_cnyInit.prodEnd || ''));
  const shipHoldStart = ref(String(_cnyInit.shipStart || ''));
  const shipHoldEnd = ref(String(_cnyInit.shipEnd || ''));
  watch([cnyMode, prodHoldStart, prodHoldEnd, shipHoldStart, shipHoldEnd], () => {
    try {
      window.localStorage.setItem(
        CNY_HOLD_KEY,
        JSON.stringify({
          mode: cnyMode.value,
          prodStart: prodHoldStart.value,
          prodEnd: prodHoldEnd.value,
          shipStart: shipHoldStart.value,
          shipEnd: shipHoldEnd.value,
        }),
      );
    } catch {
      /* 隐私模式 → 不记也行，不影响功能 ✓ */
    }
  });
  const platform = ref('');
  const status = ref('');
  const calculationWarehouse = ref('');
  const warehouse = ref('');
  const factoryRegion = ref('');
  const spu = ref('');
  const model = ref('');
  const position = ref('');
  const stockFilter = ref('');
  const dispStatuses = ref<string[]>([]);
  const skuField = ref('sku');
  const safetyDays = ref(0);
  const consolidation = ref(false);
  const targetShipmentMonth = ref('');
  const untilShipmentMonth = ref('');
  const shipmentReminderDays = ref(9);
  // 从本地恢复上次未清空的模拟草稿（1.1.96 起**永久保留** ✓，只有用户主动清空才清 ✓）
  const restoredManualState = loadManualState();
  const manualPlans = ref<ManualBatchMap>(restoredManualState.manualPlans);
  const manualPlanDrafts = ref<ManualBatchMap>(restoredManualState.manualPlanDrafts);
  /* 1.1.153（用户口径 2026-10-27）：「每次修改后，那个预览假设影响要亮起来、一动一动的，提醒去点」
     草稿改动计数 —— 每次写草稿 +1，点「预览假设影响」后把已应用版本对齐 ✓
     ⚠️ 用**计数器**而不是深比较：草稿可能上千格，每次渲染做 JSON 比较太贵 ✗ */
  const draftRevision = ref(0);
  const appliedDraftRevision = ref(0);
  /* 初始状态：上次关页面时如果草稿没推演过（草稿 ≠ 已应用），进来就该继续闪 ✓
     （只跑一次，草稿是刚解析出来的普通对象，比较成本可接受） */
  try {
    if (JSON.stringify(restoredManualState.manualPlanDrafts) !== JSON.stringify(restoredManualState.manualPlans)) {
      draftRevision.value = 1;
    }
  } catch {
    /* 比较失败就当没有未应用的改动 */
  }
  /** 有"改了但还没推演"的草稿 → 预览按钮闪烁提醒 ✓ */
  const draftDirty = computed(() => draftRevision.value !== appliedDraftRevision.value);
  // 1.1.96: 起手就把快照对齐到"刚加载进来的内容"✓ —— 否则第一次写盘会把所有已加载的键
  //   都当成"本标签页刚改过"，从而覆盖别的标签页的较新值 ✗。
  lastPersistSnapshot = { plans: restoredManualState.manualPlans, drafts: restoredManualState.manualPlanDrafts };
  /* ===== 1.1.140（用户口径 2026-10-21）：共享批次允许被「个人叉掉」=====
     语义：共享方案仍然全站生效 ✓；每个人可以把自己不要的那几条**本地排除** ✓ ——
       · 只影响**自己**这次计算/模拟（后端按 `shared_exclude` 过滤 ✓）
       · **不写库、不动全局方案、不触发全站重算** ✓（没有 plan_share 权限也能用 ✓）
       · 存在本机 localStorage（换浏览器/清缓存就没有 ✓）；签名 = `入仓日|数量`（与共享批次标识同口径 ✓）
     键 = `<skuId>_<仓点>`（与 manual_plans 同构 ✓）。 */
  const SHARED_EXCLUDE_KEY = 'replenishment.shared-excludes.v1';
  const sharedExcludes = ref<Record<string, string[]>>((() => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(SHARED_EXCLUDE_KEY) || '{}');
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, string[]>) : {};
    } catch {
      return {};
    }
  })());
  function persistSharedExcludes(): void {
    try {
      const clean = Object.fromEntries(
        Object.entries(toRaw(sharedExcludes.value)).filter(([, list]) => Array.isArray(list) && list.length),
      );
      if (Object.keys(clean).length) window.localStorage.setItem(SHARED_EXCLUDE_KEY, JSON.stringify(clean));
      else window.localStorage.removeItem(SHARED_EXCLUDE_KEY);
    } catch {
      /* 无痕模式：只当次生效 */
    }
  }
  /* 1.1.150（用户口径 2026-10-26）：共享方案按「日常 / 年前」两套独立启用 ✓
     → 排除项也必须分开记，否则在年前叉掉的批次会连带影响日常 ✗（签名是「入仓日|数量」，两边撞车很正常） */
  const sharedMode = (): 'normal' | 'cny' => (cnyMode.value ? 'cny' : 'normal');
  const excludeBucketKey = (key: string): string => `${sharedMode()}:${key}`;
  /** 当前模式下生效的排除项总数（顶栏「🚫 已排除 N 条」用 ✓） */
  const sharedExcludedCount = computed(() => {
    const prefix = `${sharedMode()}:`;
    let sum = 0;
    for (const [key, list] of Object.entries(sharedExcludes.value)) {
      if (!Array.isArray(list)) continue;
      if (key.startsWith(prefix)) sum += list.length;
      // 1.1.150 之前的存量（无前缀）只属于日常模式 ✓
      else if (sharedMode() === 'normal' && !key.includes(':')) sum += list.length;
    }
    return sum;
  });
  function isSharedExcluded(key: string, signature: string): boolean {
    if ((sharedExcludes.value[excludeBucketKey(key)] || []).includes(signature)) return true;
    // 兼容 1.1.150 之前存的无前缀排除项（当时只有日常模式 ✓）
    if (sharedMode() === 'normal' && !key.includes(':')) return (sharedExcludes.value[key] || []).includes(signature);
    return false;
  }
  /** 叉掉 / 恢复一条共享批次（**只影响本机、只影响当前模式** ✓）；改完立刻按新口径重拉一次 ✓ */
  function toggleSharedExclude(key: string, signature: string): void {
    const wasExcluded = isSharedExcluded(key, signature);
    const bucket = excludeBucketKey(key);
    const current = sharedExcludes.value[bucket] || [];
    const copy = { ...sharedExcludes.value };
    const next = wasExcluded ? current.filter((item) => item !== signature) : [...current, signature];
    if (next.length) copy[bucket] = next;
    else delete copy[bucket];
    // 顺手把旧的无前缀键里同一条清掉（否则"恢复"之后仍然生效 ✗）
    if (sharedMode() === 'normal' && Array.isArray(copy[key])) {
      const legacy = copy[key].filter((item) => item !== signature);
      if (legacy.length) copy[key] = legacy;
      else delete copy[key];
    }
    sharedExcludes.value = copy;
    persistSharedExcludes();
    invalidatePlanRows();
    void load(true);
  }
  /** 1.1.150: 只清**当前模式**的排除项（另一模式的保留 ✓ —— 两套方案互相独立） */
  function clearSharedExcludes(): void {
    const prefix = `${sharedMode()}:`;
    const next: Record<string, string[]> = {};
    for (const [key, list] of Object.entries(sharedExcludes.value)) {
      const mine = key.startsWith(prefix) || (sharedMode() === 'normal' && !key.includes(':'));
      if (!mine && Array.isArray(list) && list.length) next[key] = list;
    }
    if (Object.keys(next).length === Object.keys(sharedExcludes.value).length) return; // 本来就没有
    sharedExcludes.value = next;
    persistSharedExcludes();
    invalidatePlanRows();
    void load(true);
  }
  /* 草稿落盘；两条都清空时移除存储，不留脏数据。
     ⚠️ 这里**故意不用 `watch(..., { deep: true })`**：草稿能有上千个格子，
        deep 每次改动都要深度遍历整份草稿图重新收集依赖 —— 实测 2000 格时
        「加/删批次」单次点击从 46ms 涨到 99ms，鼠标明显发卡。
        改成在**每个改动点显式调用**，代价与草稿量无关。
     ⚠️ 新增任何写 manualPlans / manualPlanDrafts 的地方，都必须调用它。 */
  function persistManualState(): void {
    try {
      // toRaw：JSON.stringify 直接吃响应式代理的话，每个属性读都要过一层 proxy trap，
      // 实测草稿到 2000 格时单次落盘要几十毫秒。取原始对象再序列化就与数据量解耦。
      const plans = toRaw(manualPlans.value) as ManualBatchMap;
      const drafts = toRaw(manualPlanDrafts.value) as ManualBatchMap;

      // ① 求"本标签页动过哪些键"：与上一次写盘的内存快照对比（含"删除"✓）
      const prev = lastPersistSnapshot;
      const seen = new Set<string>([
        ...Object.keys(prev.plans),
        ...Object.keys(prev.drafts),
        ...Object.keys(plans),
        ...Object.keys(drafts),
      ]);
      const changed = new Set<string>();
      for (const key of seen) {
        if (
          JSON.stringify(prev.plans[key]) !== JSON.stringify(plans[key]) ||
          JSON.stringify(prev.drafts[key]) !== JSON.stringify(drafts[key])
        ) {
          changed.add(key);
        }
      }

      // ② 与存储里已有的**合并**（原来每次写的都是整份内存快照 ✗ → 多开标签页时后保存的会把
      //    另一个标签页刚填的格子整份覆盖 ✗）。
      //    规则：本标签页动过的键以本标签页为准（可能是删除 ✓）；其余键让**存储里的值优先** ✓。
      const existing = readPersistedMaps();
      const mergedPlans: ManualBatchMap = {};
      const mergedDrafts: ManualBatchMap = {};
      for (const key of new Set([...Object.keys(existing.plans), ...Object.keys(plans)])) {
        const mine = plans[key];
        if (changed.has(key)) {
          if (mine !== undefined) mergedPlans[key] = mine;
        } else {
          mergedPlans[key] = existing.plans[key] !== undefined ? existing.plans[key] : mine!;
        }
      }
      for (const key of new Set([...Object.keys(existing.drafts), ...Object.keys(drafts)])) {
        const mine = drafts[key];
        if (changed.has(key)) {
          if (mine !== undefined) mergedDrafts[key] = mine;
        } else {
          mergedDrafts[key] = existing.drafts[key] !== undefined ? existing.drafts[key] : mine!;
        }
      }

      // 记住这次写盘的内容（作为下次求差集的基准 ✓）
      lastPersistSnapshot = { plans, drafts };

      if (!Object.keys(mergedPlans).length && !Object.keys(mergedDrafts).length) {
        window.localStorage.removeItem(MANUAL_STATE_KEY);
        return;
      }
      window.localStorage.setItem(
        MANUAL_STATE_KEY,
        JSON.stringify({ savedAt: Date.now(), plans: mergedPlans, drafts: mergedDrafts }),
      );
    } catch {
      // 1.1.96: 原来这里是**静默吞掉** ✗ —— 无痕模式 / 企业策略禁用本地存储 / 配额满时，
      //   用户以为填好了，一刷新全没了还找不到原因。现在明确提示一次 ✓。
      if (!manualPersistWarned) {
        manualPersistWarned = true;
        try {
          useAppStore().notify(
            '模拟批次没能保存到本机浏览器（可能是无痕模式或浏览器禁用了本地存储）—— 刷新后可能会丢失',
            'error',
          );
        } catch {
          /* 提示本身失败不影响业务 */
        }
      }
    }
  }
  const previewMode = ref(false);
  const sort = ref('daily_avg');
  const dir = ref<'asc' | 'desc'>('desc');
  let controller: AbortController | null = null;
  let activeRowsRequestSignature: string | null = null;
  let lastRowsLoadedAt = 0;
  let rowsCacheSignature: string | null = null;
  let rowsInflight = 0;
  let rowsRequestSequence = 0;
  let planRowsCacheSignature: string | null = null;
  let planRowsGeneration = 0;
  let planRowsRequestSequence = 0;
  let latestPlanRowsRequestSequence = 0;
  const planRowsRequests = new Map<string, PlanRowsRequest>();
  const responseCache = new Map<string, { payload: ReplenishmentResponse; ts: number }>();
  let referenceFactoryOrdersPromise: Promise<void> | null = null;

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)));

  function params(includePage = true): URLSearchParams {
    const result = new URLSearchParams({
      page: String(page.value),
      page_size: String(pageSize.value),
      ship_type: shipType.value,
      sort: sort.value || 'daily_avg',
      dir: dir.value,
    });
    if (!includePage) {
      result.delete('page');
      result.delete('page_size');
    }
    // 1.1.113: 年前备货 —— **只有开了才带参数** ✓（不带 = 后端行为与现在完全一致 ✓）
    if (cnyMode.value) {
      if (prodHoldStart.value) result.set('prod_hold_start', prodHoldStart.value);
      if (prodHoldEnd.value) result.set('prod_hold_end', prodHoldEnd.value);
      if (shipHoldStart.value) result.set('ship_hold_start', shipHoldStart.value);
      if (shipHoldEnd.value) result.set('ship_hold_end', shipHoldEnd.value);
    }
    if (query.value.trim()) {
      result.set('sku_code', query.value.trim());
      result.set('sku_field', skuField.value);
    }
    if (platform.value) result.set('platform', platform.value);
    if (status.value) result.set('status_filter', status.value);
    if (calculationWarehouse.value) result.set('wh_code', calculationWarehouse.value);
    if (warehouse.value) result.set('wh_code', warehouse.value);
    if (factoryRegion.value) result.set('factory_region', factoryRegion.value);
    if (spu.value) result.set('spu', spu.value);
    if (model.value) result.set('model', model.value);
    if (position.value) result.set('position', position.value);
    if (stockFilter.value) result.set('stock_filter', stockFilter.value);
    if (dispStatuses.value.length) result.set('disp_status', dispStatuses.value.join(','));
    if (safetyDays.value) result.set('safety_days', String(safetyDays.value));
    if (activityIds.value.length) result.set('activity_ids', activityIds.value.join(','));
    if (consolidation.value) result.set('consolidation', '1');
    if (targetShipmentMonth.value) result.set('target_shipment_month', targetShipmentMonth.value);
    if (untilShipmentMonth.value) result.set('until_shipment_month', untilShipmentMonth.value);
    result.set('shipment_reminder_days', String(shipmentReminderDays.value));
    if (Object.keys(manualPlans.value).length) result.set('manual_plans', JSON.stringify(manualPlans.value));
    // 1.1.140: 个人叉掉的共享批次 —— 不写库 ✓，后端按它过滤（别人不受影响 ✓）
    // 1.1.150: 只传**当前模式**的那批，并把 `cny:` / `normal:` 前缀去掉（后端按下标 `skuId_wh` 解析 ✓）
    const excludesForMode = (() => {
      const prefix = `${sharedMode()}:`;
      const out: Record<string, string[]> = {};
      for (const [key, list] of Object.entries(toRaw(sharedExcludes.value))) {
        if (!Array.isArray(list) || !list.length) continue;
        if (key.startsWith(prefix)) out[key.slice(prefix.length)] = list;
        else if (sharedMode() === 'normal' && !key.includes(':')) out[key] = list; // 存量无前缀 → 日常
      }
      return out;
    })();
    if (Object.keys(excludesForMode).length) result.set('shared_exclude', JSON.stringify(excludesForMode));
    return result;
  }

  function isAbortError(cause: unknown): boolean {
    return Boolean(cause && typeof cause === 'object' && 'name' in cause && cause.name === 'AbortError');
  }

  function planRowsSignature(): string {
    return params(false).toString();
  }

  function invalidatePlanRows(): void {
    planRows.value = [];
    planRowsCacheSignature = null;
    planRowsGeneration += 1;
    latestPlanRowsRequestSequence = ++planRowsRequestSequence;
    for (const request of planRowsRequests.values()) request.controller.abort();
    planRowsRequests.clear();
    planRowsLoading.value = false;
  }

  function invalidateRowsCache(): void {
    responseCache.clear();
    rowsCacheSignature = null;
    lastRowsLoadedAt = 0;
    invalidatePlanRows();
  }

  /**
   * 1.1.94: SKU 主数据（日均销量等）被改后，由 SKU 管理页调用 —— 备货数据要**无感**跟着变。
   *
   * 为什么需要：备货行在前端有缓存（`ROWS_CACHE_MS` + `rowsCacheSignature`）✗，
   *   改完 SKU 直接切到备货页会命中旧缓存 → 表现为"**必须手动刷新才同步**"✗（用户反馈）。
   * 做法：清掉缓存 + **后台静默预取一次**（不阻塞、不弹加载态）→ 下次进备货页就是新数 ✓。
   */
  function notifySkuChanged(): void {
    invalidateRowsCache();
    void load(true).catch(() => {
      /* 预取失败无所谓：缓存已清，进页面时会正常重新加载 */
    });
  }

  function applyRowsResult(result: ReplenishmentResponse, signature: string): void {
    rows.value = result.rows || [];
    total.value = result.total || 0;
    totals.value = result.totals || {};
    kpis.value = result.kpis || {};
    dispCounts.value = result.disp_counts || {};
    summary.value = (result.summary || []) as Record<string, unknown>[];
    containers.value = (result.containers || []) as Record<string, unknown>[];
    whCodes.value = result.wh_codes || [];
    /* 1.1.76: 候选项原来是从「本次返回的行」里推的 —— 行随筛选/分页变窄，候选项也跟着变窄，
       表现就是"库存板块看得到的 SPU，备货计划下拉里没有；但直接输入搜索又能出来"
       （搜索是直接查服务端，不走这个候选列表）。
       改为从**共享的 SKU 全量**取（sharedSkus 本身是缓存，代价很小），候选列表才稳定。 */
    if (result.filter_options) filterOptions.value = result.filter_options;
    else {
      const buildFromSkus = () => {
        const activeSkus = ((sharedSkus.rows || []) as Record<string, unknown>[]).filter((row) => row.active !== 0);
        const values = (key: string) =>
          [...new Set(activeSkus.map((row) => String(row[key] || '')).filter(Boolean))].sort();
        filterOptions.value = {
          factory_regions: values('factory_region'),
          spus: values('spu'),
          models: values('model'),
          positions: values('product_position'),
        };
      };
      // 已加载就直接用；没加载完则等它回来再补（不阻塞本次渲染）
      if (sharedSkus.rows?.length) buildFromSkus();
      else void sharedSkus.ensureLoaded().then(buildFromSkus);
    }
    invalidatePlanRows();
    rowsCacheSignature = signature;
    lastRowsLoadedAt = Date.now();
  }

  async function setManualPlan(
    row: ReplenishmentRow,
    batches: Array<{ date: string; qty: number; fo?: string }>,
  ): Promise<void> {
    const key = `${row.sku_id}_${row.wh_code}`;
    if (batches.length) manualPlans.value = { ...manualPlans.value, [key]: { batches } };
    else {
      const copy = { ...manualPlans.value };
      delete copy[key];
      manualPlans.value = copy;
    }
    persistManualState();
    invalidatePlanRows();
    // 1.1.95: 这类改动也会整表重算 → 保持滚动位置 ✓（别把用户弹回最上面）
    await keepScrollWhile(() => load(true));
  }

  function resetManualState(): void {
    manualPlans.value = {};
    manualPlanDrafts.value = {};
    previewMode.value = false;
    appliedDraftRevision.value = draftRevision.value; // 清空后没有"未应用修改" → 停止闪 ✓
    // 1.1.140: 「清空模拟」= 回到真实口径 → 个人叉掉的共享批次也一起复原 ✓（否则用户以为清干净了 ✗）
    sharedExcludes.value = {};
    persistSharedExcludes();
    persistManualState();
    invalidatePlanRows();
  }

  function clearManualPlans(): void {
    // 1.1.95: 「清空模拟 / 恢复真实」后保持滚动位置 ✓
    void keepScrollWhile(async () => {
      resetManualState();
      await load(true);
    });
  }

  async function loadAllPlanRows(): Promise<ReplenishmentRow[]> {
    const signature = planRowsSignature();
    if (planRows.value.length && planRowsCacheSignature === signature) return planRows.value;

    const existing = planRowsRequests.get(signature);
    if (existing) {
      latestPlanRowsRequestSequence = existing.sequence;
      planRowsLoading.value = true;
      return existing.promise;
    }

    const generation = planRowsGeneration;
    const sequence = ++planRowsRequestSequence;
    latestPlanRowsRequestSequence = sequence;
    const requestController = new AbortController();
    const requestToken = Symbol(signature);
    planRowsLoading.value = true;

    const requestPromise = (async () => {
      try {
        const result = await http.get<{ rows: ReplenishmentRow[] }>(
          `/api/replenishment/plan-rows?${signature}`,
          requestController.signal,
        );
        const resultRows = result.rows || [];
        const isCurrent =
          generation === planRowsGeneration &&
          sequence === latestPlanRowsRequestSequence &&
          signature === planRowsSignature();
        if (isCurrent) {
          planRows.value = resultRows;
          planRowsCacheSignature = signature;
          return resultRows;
        }
        if (planRowsRequests.get(signature)?.token === requestToken) planRowsRequests.delete(signature);
        return loadAllPlanRows();
      } catch (cause) {
        if (isAbortError(cause) && generation !== planRowsGeneration) return loadAllPlanRows();
        throw cause;
      } finally {
        if (planRowsRequests.get(signature)?.token === requestToken) planRowsRequests.delete(signature);
        if (sequence === latestPlanRowsRequestSequence) planRowsLoading.value = false;
      }
    })();
    const request: PlanRowsRequest = {
      controller: requestController,
      promise: requestPromise,
      sequence,
      token: requestToken,
    };
    planRowsRequests.set(signature, request);
    return requestPromise;
  }

  async function load(force = false, options: LoadOptions = {}): Promise<void> {
    const signature = params().toString();
    let silent = Boolean(options.silent);
    const hasCachedRows = rowsCacheSignature !== null;
    if (!force && !silent && hasCachedRows) silent = true;
    if (!force && rowsCacheSignature === signature && Date.now() - lastRowsLoadedAt < ROWS_CACHE_MS) {
      if (controller && activeRowsRequestSignature !== signature) {
        controller.abort();
        rowsRequestSequence += 1;
      }
      error.value = '';
      return;
    }
    const cacheable = !params().has('manual_plans');
    const cached = !force && cacheable ? responseCache.get(signature) : undefined;
    if (cached && Date.now() - cached.ts < RESPONSE_CACHE_MS) {
      controller?.abort();
      rowsRequestSequence += 1;
      responseCache.delete(signature);
      responseCache.set(signature, cached);
      applyRowsResult(cached.payload, signature);
      error.value = '';
      return;
    }

    controller?.abort();
    const requestController = new AbortController();
    controller = requestController;
    activeRowsRequestSignature = signature;
    const sequence = ++rowsRequestSequence;
    if (!silent) {
      rowsInflight += 1;
      loading.value = true;
    }
    error.value = '';
    try {
      const result = await http.get<ReplenishmentResponse>(`/api/replenishment?${signature}`, requestController.signal);
      if (sequence !== rowsRequestSequence) return;
      applyRowsResult(result, signature);
      if (cacheable) {
        responseCache.set(signature, { payload: result, ts: Date.now() });
        while (responseCache.size > RESPONSE_CACHE_MAX) responseCache.delete(responseCache.keys().next().value!);
      }
    } catch (cause) {
      if (sequence === rowsRequestSequence && !isAbortError(cause))
        error.value = cause instanceof Error ? cause.message : '加载失败';
    } finally {
      if (controller === requestController) {
        controller = null;
        activeRowsRequestSignature = null;
      }
      if (!silent) {
        rowsInflight = Math.max(0, rowsInflight - 1);
        if (rowsInflight === 0) loading.value = false;
      }
    }
  }

  async function loadPlatforms(): Promise<void> {
    if (platforms.value.length) return;
    try {
      const response = await http.get<{ platforms: string[] }>('/api/sales/platforms');
      platforms.value = (response.platforms || []).filter(Boolean);
    } catch {
      platforms.value = [];
    }
  }

  async function loadActivities(): Promise<void> {
    try {
      await sharedActivities.ensureLoaded();
      activities.value = sharedActivities.rows || [];
      refreshActivityIds();
    } catch {
      activities.value = [];
      activityIds.value = [];
    }
  }

  function parseActivityIds(value: unknown): number[] {
    if (Array.isArray(value)) return value.map(Number).filter(Number.isFinite);
    if (typeof value !== 'string' || !value.trim()) return [];
    try {
      const parsed: unknown = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : [];
    } catch {
      return [];
    }
  }

  function activityDate(value: unknown): number | null {
    const parts = String(value || '')
      .split('-')
      .map(Number);
    return parts.length >= 2 && parts[0] && parts[1] ? parts[0] * 100 + parts[1] : null;
  }

  function refreshActivityIds(): void {
    const bound = new Set<number>();
    skus.value.forEach((sku) => parseActivityIds(sku.activity_ids).forEach((id) => bound.add(id)));
    const now = new Date();
    const today = (now.getMonth() + 1) * 100 + now.getDate();
    activityIds.value = activities.value
      .filter((activity) => bound.has(Number(activity.id)) && Number(activity.active) === 1)
      .filter((activity) => {
        const start = activityDate(activity.start_date);
        const end = activityDate(activity.end_date);
        if (start == null || end == null || start > end) return true;
        return end >= today;
      })
      .map((activity) => Number(activity.id));
  }

  async function loadSkus(): Promise<void> {
    try {
      await sharedSkus.ensureLoaded();
      skus.value = sharedSkus.rows || [];
      const activeSkus = skus.value.filter((row) => row.active !== 0);
      const values = (key: string) =>
        [...new Set(activeSkus.map((row) => String(row[key] || '')).filter(Boolean))].sort();
      filterOptions.value = {
        factory_regions: values('factory_region'),
        spus: values('spu'),
        models: values('model'),
        positions: ['战略', '正常', '正常新品', '潜力新品', '清货'],
      };
      refreshActivityIds();
    } catch {
      skus.value = [];
    }
  }

  async function loadRatioMeta(): Promise<void> {
    try {
      ratioMeta.value = await http.get<Record<string, unknown>>('/api/ratios/meta');
    } catch {
      ratioMeta.value = null;
    }
  }

  function setManualDraft(row: ReplenishmentRow, batches: Array<{ date: string; qty: number; fo?: string }>): void {
    const key = `${row.sku_id}_${row.wh_code}`;
    // An empty draft must remain as an explicit override when an applied preview
    // exists. Deleting the key here would make the UI fall back to manualPlans
    // and resurrect every removed batch.
    //
    // ⚠️ 只改这一格，**不要**整份替换 manualPlanDrafts：换了引用会让明细表里
    //    每一行的单元格全部重跑渲染（实测 20 行 × 26 列 ≈ 520 个单元格，
    //    「加/删批次」单次点击白付 ~40ms）。就地增删一个键，只有那一行会重渲染。
    if (batches.length || Object.prototype.hasOwnProperty.call(manualPlans.value, key)) {
      manualPlanDrafts.value[key] = { batches };
    } else {
      delete manualPlanDrafts.value[key];
    }
    draftRevision.value += 1; // 有改动 → 预览按钮开始闪 ✓
    persistManualState();
  }

  async function previewManualPlans(): Promise<void> {
    // 1.1.95: 推演前记下滚动位置、推演完放回去 —— 用户点完「模拟」不该被弹回表格最上面 ✗（用户反馈）
    await keepScrollWhile(async () => {
      manualPlans.value = JSON.parse(JSON.stringify(manualPlanDrafts.value));
      previewMode.value = true;
      appliedDraftRevision.value = draftRevision.value; // 已按当前草稿推演 → 停止闪烁 ✓
      persistManualState();
      invalidatePlanRows();
      await load(true);
    });
  }

  async function exitPreview(): Promise<void> {
    // 1.1.95: 「恢复真实」同样保持滚动位置 ✓
    await keepScrollWhile(async () => {
      manualPlans.value = {};
      manualPlanDrafts.value = Object.fromEntries(
        Object.entries(manualPlanDrafts.value).filter(([, plan]) => plan.batches.length > 0),
      );
      previewMode.value = false;
      appliedDraftRevision.value = draftRevision.value; // 已回到真实口径 → 不再闪（草稿保留，可再点预览）
      persistManualState();
      invalidatePlanRows();
      await load(true);
    });
  }

  async function loadReferenceFactoryOrders(): Promise<void> {
    if (referenceFactoryOrdersLoaded.value) return;
    if (referenceFactoryOrdersPromise) return referenceFactoryOrdersPromise;
    referenceFactoryOrdersLoading.value = true;
    const request = (async () => {
      try {
        const response = await http.get<{ rows: Record<string, unknown>[] }>('/api/factory-orders?limit=200');
        referenceFactoryOrders.value = response.rows || [];
        referenceFactoryOrdersLoaded.value = true;
      } catch (cause) {
        error.value = cause instanceof Error ? cause.message : '工厂订单加载失败';
      } finally {
        referenceFactoryOrdersLoading.value = false;
        referenceFactoryOrdersPromise = null;
      }
    })();
    referenceFactoryOrdersPromise = request;
    return request;
  }

  async function loadSimulation(row = simulationRow.value): Promise<void> {
    if (!row) return;
    simulationRow.value = row;
    simulationDays.value = 30;
    simulationOpen.value = true;
    simulationLoading.value = true;
    simulationError.value = '';
    const query = params(false);
    query.set('sku_id', String(row.sku_id));
    query.set('sim_days', '365');
    try {
      await loadAllPlanRows();
      simulation.value = await http.get<Record<string, unknown>>(`/api/sku-day-sim?${query}`);
    } catch (cause) {
      simulationError.value = cause instanceof Error ? cause.message : '逐日模拟加载失败';
    } finally {
      simulationLoading.value = false;
    }
  }
  function closeSimulation(): void {
    simulationOpen.value = false;
  }
  /**
   * 批量导出逐日模拟用：按 sku_id 单独取一份逐日明细（与弹窗同源同口径）。
   * 不复用 simulation.value，因为批量导出要连续取多个 SKU、不能互相覆盖。
   */
  async function fetchSkuDailySim(
    skuId: number,
    days = 365,
    /* 1.1.124: 出货计划导出用 —— 可以显式指定「模拟批」与「计划批」两套到货事件 ✓
       · manualPlans: 覆盖默认的"已应用草稿"（导出要跟界面显示一致：预览中=已应用，否则=草稿 ✓）
       · plannedArrivals: 系统建议的未来批次，按 skuId_仓点 分组 ✓（让导出的曲线也算上计划批 ✓） */
    options: {
      manualPlans?: ManualBatchMap;
      plannedArrivals?: Record<string, Array<{ date: string; qty: number }>>;
    } = {},
  ): Promise<DailySimulationData | null> {
    const query = params(false);
    query.set('sku_id', String(skuId));
    query.set('sim_days', String(days));
    if (options.manualPlans) {
      if (Object.keys(options.manualPlans).length) query.set('manual_plans', JSON.stringify(options.manualPlans));
      else query.delete('manual_plans');
    }
    if (options.plannedArrivals && Object.keys(options.plannedArrivals).length) {
      query.set('planned_arrivals', JSON.stringify(options.plannedArrivals));
    }
    const response = await http.get<{ raw?: DailySimulationData }>(`/api/sku-day-sim?${query}`);
    return response.raw ?? null;
  }
  async function loadFactoryOrders(row: ReplenishmentRow): Promise<void> {
    factoryOrdersLoading.value = true;
    factoryOrdersError.value = '';
    try {
      const response = await http.get<{ rows: Record<string, unknown>[]; group_skus?: string[] }>(
        `/api/factory-orders?sku_code=${encodeURIComponent(row.sku_code)}&with_group=1`,
      );
      factoryOrders.value = response.rows || [];
      factoryOrderGroupSkus.value = response.group_skus || [row.sku_code];
    } catch (cause) {
      factoryOrdersError.value = cause instanceof Error ? cause.message : '工厂订单加载失败';
    } finally {
      factoryOrdersLoading.value = false;
    }
  }

  /* ===== 1.1.99: 共享方案（服务端保存、全站生效 ✓）=====
     用户口径：有一类人（默认 owner/super_admin/admin，可在权限管理里按人/部门授予）能把
     当前模拟草稿**保存成共享方案**，全系统的人都看得到；方案里引用工厂订单的批次
     会**随最新数据变化** —— 单子没了 / 剩余为 0，那条批次就自动消失（后端加载时刷新 ✓）。 */
  const sharedPlans = ref<Array<Record<string, unknown>>>([]);
  const activeSharedPlan = ref<Record<string, unknown> | null>(null);
  const sharedPlansLoading = ref(false);
  /* 1.1.135（用户反馈：共享方案"总是不稳定"、换筛选后变「共享方案暂无」）：
     失败后 2 秒自动静默重试（只重试一次，避免打爆接口）✓；
     成功拿到数据就清掉定时器 ✓ */
  let sharedPlanRetryTimer: ReturnType<typeof setTimeout> | null = null;
  /* 1.1.143：重试**必须封顶** —— 原来是"失败→2 秒后重试→又失败→再排 2 秒…"，
     服务端宕机时会变成**永久每 2 秒打一次** ✗（控制台/网关日志被刷爆）。现在最多 3 次 ✓ */
  let sharedPlanRetryCount = 0;
  const SHARED_PLAN_RETRY_MAX = 3;
  function scheduleSharedPlanRetry(): void {
    if (sharedPlanRetryTimer !== null) return;
    if (sharedPlanRetryCount >= SHARED_PLAN_RETRY_MAX) return;
    sharedPlanRetryCount += 1;
    sharedPlanRetryTimer = setTimeout(() => {
      sharedPlanRetryTimer = null;
      void loadSharedPlans({ quiet: true });
    }, 2000);
  }

  /** quiet=true：**不点亮**「加载中」（弹窗里的表不闪 ✗）—— 增删改后的静默刷新用 ✓
   *  1.1.136：这个接口现在是**纯读**（后端不再顺手刷新工厂订单 ✓）→ 又快又不会因为外部表抖动返回空 ✓ */
  async function loadSharedPlans(options: { quiet?: boolean } = {}): Promise<void> {
    if (!options.quiet) sharedPlansLoading.value = true;
    try {
      const res = await http.get<{ plans: Array<Record<string, unknown>>; active: Record<string, unknown> | null }>(
        // 1.1.150: 按当前模式取（日常 / 年前 各自一套，互不影响 ✓）
        `/api/replenishment/shared-plans?mode=${sharedMode()}`,
      );
      sharedPlans.value = res.plans || [];
      activeSharedPlan.value = res.active || null;
      if (sharedPlanRetryTimer !== null) {
        clearTimeout(sharedPlanRetryTimer);
        sharedPlanRetryTimer = null;
      }
      sharedPlanRetryCount = 0; // 成功即清零 ✓（下次真失败还能再试 3 次）
      // 明细数值按最新工厂订单刷新（独立接口 ✓，慢也只慢它自己，不影响列表/按钮 ✓）
      void refreshActiveSharedPlan();
    } catch (_) {
      /* 1.1.134: 取失败**不要清空已有的** ✗ —— 清空会让工具栏上的「🧩 共享方案」入口直接消失
         （表现为"共享方案那块设置看不到了"✗）。保留上一次成功的结果，等下次刷新再更新 ✓。 */
      if (!sharedPlans.value.length) activeSharedPlan.value = null;
      scheduleSharedPlanRetry();
    } finally {
      if (!options.quiet) sharedPlansLoading.value = false;
    }
  }

  /** 1.1.136：单独拉「按最新工厂订单刷新后」的启用方案明细
   *  · 后端会按 5 分钟缓存 + 单飞 ✓，这里多调几次也不会把工厂订单表打爆 ✓
   *  · 失败只影响"这批明细的数字"，列表与按钮完全不受影响 ✓（所以不需要兜底逻辑） */
  async function refreshActiveSharedPlan(): Promise<void> {
    try {
      const res = await http.get<{ active: Record<string, unknown> | null }>(
        `/api/replenishment/shared-plans/active/refreshed?mode=${sharedMode()}`,
      );
      if (res && res.active) activeSharedPlan.value = { ...(activeSharedPlan.value || {}), ...res.active };
    } catch (_) {
      /* 列表那份已经渲染好了；这里失败就保持列表的批次（不值得打扰用户） */
    }
  }

  /* 1.1.150（用户口径 2026-10-26）：切「日常 / 年前」时，共享方案整套换掉 ✓
     —— 两套独立启用：年前里启用 A、日常里启用 B，来回切各自看到自己的那套 ✓
     先清空再拉，避免切换瞬间页面上还挂着另一个模式的批次（会让「计划出货时间」列短暂多出几行 ✗） */
  watch(cnyMode, () => {
    activeSharedPlan.value = null;
    sharedPlans.value = [];
    void loadSharedPlans({ quiet: true });
  });

  /** 1.1.150（用户口径 2026-10-26）：「改变了共享方案后，保存按钮要能亮起来」
   *  把「生效方案里**没被本机排除**的批次」还原成草稿形态 → 叉掉几条就能直接存成新方案 ✓
   *  · `fo`（工厂订单标识）原样带回去 → 后端 stampSharedPayloadSources 仍按工厂单刷新 ✓ 不退化
   *  · 日期/数量用前端当前这份（若已按最新工厂订单刷新过，存下来的就是刷新后的值 ✓） */
  function activePlanPayloadWithoutExcludes(): ManualBatchMap | null {
    const plan = activeSharedPlan.value as { batches?: Array<Record<string, unknown>> } | null;
    if (!plan || !Array.isArray(plan.batches) || !plan.batches.length) return null;
    const out: ManualBatchMap = {};
    for (const batch of plan.batches) {
      const skuId = Number(batch.sku_id) || 0;
      const wh = String(batch.wh_code || '');
      const date = String(batch.date || '').slice(0, 10);
      const qty = Number(batch.qty) || 0;
      if (!skuId || !wh || !date || qty <= 0) continue;
      const key = `${skuId}_${wh}`;
      if (isSharedExcluded(key, `${date}|${qty}`)) continue;
      const bucket = out[key] || (out[key] = { batches: [] });
      const item: { date: string; qty: number; fo?: string } = { date, qty };
      if (batch.fo) item.fo = String(batch.fo);
      bucket.batches.push(item);
    }
    return Object.keys(out).length ? out : null;
  }

  /** 保存时用的 payload：
   *  ① 自己填的模拟草稿（原样 ✓）
   *  ② 若本机排除了共享批次 → 再把「生效方案里剩下的批次」并进来 ✓
   *     （这样保存的是"你现在看到的那一版"，而不是只有草稿那几格）
   *  没有任何可存的内容时返回空对象 ✓ */
  function buildSharedPayload(): ManualBatchMap {
    const own = toRaw(manualPlanDrafts.value) as ManualBatchMap;
    const payload: ManualBatchMap = {};
    for (const [key, value] of Object.entries(own)) {
      const batches = Array.isArray(value?.batches) ? value.batches : [];
      if (batches.length) payload[key] = { batches: batches.map((batch) => ({ ...batch })) };
    }
    if (sharedExcludedCount.value > 0) {
      const rest = activePlanPayloadWithoutExcludes();
      if (rest) {
        for (const [key, value] of Object.entries(rest)) {
          const bucket = payload[key] || (payload[key] = { batches: [] });
          bucket.batches.push(...value.batches);
        }
      }
    }
    return payload;
  }

  /** 保存成功后把本机排除项清掉（**安静清**，不自己再触发一次重算 ✗）：
   *  排除签名是「入仓日|数量」，新方案里可能重新出现同样的签名 → 不清就会继续误伤新方案 ✓ */
  function clearSharedExcludesQuiet(): void {
    const prefix = `${sharedMode()}:`;
    const next: Record<string, string[]> = {};
    for (const [key, list] of Object.entries(sharedExcludes.value)) {
      const mine = key.startsWith(prefix) || (sharedMode() === 'normal' && !key.includes(':'));
      if (!mine && Array.isArray(list) && list.length) next[key] = list;
    }
    if (Object.keys(next).length === Object.keys(sharedExcludes.value).length) return;
    sharedExcludes.value = next;
    persistSharedExcludes();
  }

  /** 把当前模拟草稿保存成共享方案（需要 button.replenishment.plan_share ✓；服务端会把它设为"唯一生效"✓）
   *  1.1.139：带上「我看到的生效方案 id」→ 服务端做并发校验（别人刚保存过就 409 ✓，不再静默覆盖 ✗） */
  async function saveSharedPlan(name: string, note: string): Promise<{ ok: boolean; msg: string }> {
    const payload = buildSharedPayload();
    if (!Object.keys(payload).length) {
      return {
        ok: false,
        msg: '没有可保存的内容：先填「计划出货时间 / 计划出货数量」批次，或在生效方案里叉掉几条再保存 ✓',
      };
    }
    try {
      const res = await http.post<{ batch_count: number; name: string; cleaned?: number }>(
        '/api/replenishment/shared-plans',
        {
          name,
          note,
          payload,
          // 1.1.150: 存到当前模式（日常 / 年前），只影响该模式的启用状态 ✓
          mode: sharedMode(),
          expected_enabled_id: Number(activeSharedPlan.value?.id) || 0, // 0 = 我认为当前没有生效方案 ✓
        },
      );
      await loadSharedPlans({ quiet: true });
      // 1.1.150: 已经把"排除后的口径"存成新方案 → 本机排除项作废（否则会继续误伤新方案里同签名的批次 ✗）
      clearSharedExcludesQuiet();
      invalidatePlanRows();
      await load(true);
      // 1.1.129: 服务端只保留最新 20 个；被清理掉几个也如实告诉用户 ✓（不是偷偷删 ✗）
      const cleaned = Number(res.cleaned) || 0;
      return {
        ok: true,
        msg: `已保存共享方案「${res.name}」：${res.batch_count} 条批次（全站生效 ✓）${
          cleaned > 0 ? ` · 已自动清理更早的 ${cleaned} 个（只保留最新 20 个）` : ''
        }`,
      };
    } catch (cause) {
      // 1.1.139: 409 = 期间别人保存/停用了方案 → 顺手刷新列表，让界面立刻显示"现在生效的是哪一套" ✓
      if (cause instanceof HttpError && cause.status === 409) void loadSharedPlans({ quiet: true });
      return { ok: false, msg: cause instanceof Error ? cause.message : '保存失败' };
    }
  }

  /** 改名 / 备注 / 启用停用（启用时会自动停用旧的 ✓ —— 全站同时只有一套生效 ✓）
   *  1.1.116: 只有「启停」会改变备货数字 ✓ → 才整页重算；改个名字/备注不重算 ✗（原来一律重算，白等） */
  async function updateSharedPlan(
    id: number,
    patch: { name?: string; note?: string; enabled?: boolean },
  ): Promise<{ ok: boolean; msg: string }> {
    try {
      await http.put(`/api/replenishment/shared-plans/${id}`, patch);
      await loadSharedPlans({ quiet: true });
      if (patch.enabled !== undefined) {
        invalidatePlanRows();
        await load(true);
      }
      return { ok: true, msg: patch.enabled === true ? '已启用该方案（旧的自动停用 ✓）' : '已更新 ✓' };
    } catch (cause) {
      return { ok: false, msg: cause instanceof Error ? cause.message : '更新失败' };
    }
  }

  /** 删除共享方案
   *  1.1.116（用户反馈："删除个共享方案也需要加载"✗）：
   *   · **只有删的是「生效中」的那套** 才需要整页重算 ✓（数字来自它）；
   *     其它方案删掉不影响任何备货数字 → 直接从本地列表摘掉 ✓，不刷新页面、不闪加载 ✗；
   *   · 列表刷新用 quiet ✓（弹窗里的表格不会闪成"加载中…"✗）。 */
  async function deleteSharedPlan(id: number): Promise<{ ok: boolean; msg: string }> {
    try {
      const wasActive = Number(activeSharedPlan.value?.id) === Number(id);
      await http.delete(`/api/replenishment/shared-plans/${id}`);
      sharedPlans.value = sharedPlans.value.filter((p) => Number(p.id) !== Number(id));
      if (wasActive) {
        activeSharedPlan.value = null;
        invalidatePlanRows();
        await load(true);
        return { ok: true, msg: '已删除 ✓（删掉的是生效中的方案，数字已重新计算）' };
      }
      return { ok: true, msg: '已删除 ✓' };
    } catch (cause) {
      return { ok: false, msg: cause instanceof Error ? cause.message : '删除失败' };
    }
  }

  return {
    notifySkuChanged,
    rows,
    total,
    page,
    pageSize,
    pageCount,
    totals,
    kpis,
    dispCounts,
    summary,
    containers,
    whCodes,
    platforms,
    activities,
    skus,
    activityIds,
    filterOptions,
    ratioMeta,
    planRows,
    planRowsLoading,
    simulationOpen,
    simulationLoading,
    simulationError,
    simulationDays,
    simulationRow,
    simulation,
    factoryOrders,
    factoryOrderGroupSkus,
    factoryOrdersLoading,
    factoryOrdersError,
    referenceFactoryOrders,
    referenceFactoryOrdersLoading,
    referenceFactoryOrdersLoaded,
    loading,
    error,
    query,
    shipType,
    // 1.1.113: 年前备货模式
    cnyMode,
    prodHoldStart,
    prodHoldEnd,
    shipHoldStart,
    shipHoldEnd,
    platform,
    status,
    calculationWarehouse,
    warehouse,
    factoryRegion,
    spu,
    model,
    position,
    stockFilter,
    dispStatuses,
    skuField,
    safetyDays,
    consolidation,
    targetShipmentMonth,
    untilShipmentMonth,
    shipmentReminderDays,
    manualPlans,
    manualPlanDrafts,
    previewMode,
    manualPlanBusy,
    sort,
    dir,
    load,
    setManualPlan,
    resetManualState,
    clearManualPlans,
    invalidatePlanRows,
    invalidateRowsCache,
    loadAllPlanRows,
    loadSimulation,
    closeSimulation,
    fetchSkuDailySim,
    loadFactoryOrders,
    loadPlatforms,
    loadActivities,
    loadSkus,
    loadRatioMeta,
    setManualDraft,
    draftDirty,
    previewManualPlans,
    exitPreview,
    loadReferenceFactoryOrders,
    // 1.1.99: 共享方案
    sharedPlans,
    activeSharedPlan,
    sharedPlansLoading,
    // 1.1.140: 个人叉掉的共享批次（本地口径 ✓）
    sharedExcludes,
    sharedExcludedCount,
    isSharedExcluded,
    toggleSharedExclude,
    clearSharedExcludes,
    loadSharedPlans,
    refreshActiveSharedPlan,
    saveSharedPlan,
    updateSharedPlan,
    deleteSharedPlan,
  };
});
